import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { UserCheck, UserX } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatDateTime, hasPermission, labelOf, MESSAGE_DIRECTION_LABELS, MESSAGE_THREAD_STATUS, messageReplySchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  DescriptionList,
  DocumentList,
  ErrorState,
  FileDropzone,
  Form,
  FormRootError,
  Label,
  PageHeader,
  Select,
  StatusBadge,
  TextareaField,
  toast,
} from '@blimo/ui';
import { DetailSkeleton } from '../../../components/data.jsx';

const STATUS_OPTIONS = Object.entries(MESSAGE_THREAD_STATUS).map(([value, meta]) => ({ value, label: meta.label }));

export function InboxThreadPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { threadId } = useParams({ strict: false });
  const { data: thread, error, isPending, refetch } = useQuery(adminQueries.message(api, threadId));

  const breadcrumb = (
    <Link to="/kommunikation/posteingang" className="hover:underline">
      Posteingang
    </Link>
  );
  if (error) {
    return (
      <>
        <PageHeader title="Nachricht" breadcrumb={breadcrumb} />
        <ErrorState error={error} onRetry={() => refetch()} notFoundTitle="Nachricht nicht gefunden" />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;
  const canReply = hasPermission(me, 'messages.reply');

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={thread.subject}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge domain="messageThread" status={thread.status} />
            <span>{thread.property?.name}</span>
            {thread.unit && <span>· {[thread.unit.number, thread.unit.designation].filter(Boolean).join(' · ')}</span>}
          </span>
        }
      />
      <div className="grid gap-3 lg:grid-cols-[1fr_20rem]">
        <div className="flex min-w-0 flex-col gap-3">
          <Timeline thread={thread} />
          {canReply && <ReplyForm threadId={thread.id} />}
        </div>
        <div className="flex flex-col gap-3">
          <Card>
            <CardHeader>
              <CardTitle as="h2">Vorgang</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <DescriptionList
                labelWidth="7rem"
                items={[
                  { label: 'Von', value: thread.creator ? `${thread.creator.name}` : null, hint: thread.creator?.email },
                  { label: 'Eröffnet', value: thread.created_at ? formatDateTime(thread.created_at) : null },
                  { label: 'Zuständig', value: thread.assignee?.name ?? 'niemand' },
                ]}
              />
              {canReply && <ThreadControls thread={thread} me={me} />}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}

function Timeline({ thread }) {
  const api = useApi();
  const messages = thread.messages ?? [];
  return (
    <ol aria-label="Nachrichtenverlauf" className="flex flex-col gap-3">
      {messages.map((m) => {
        const outbound = m.direction === 'outbound';
        return (
          <li key={m.id} className={cn('flex', outbound ? 'justify-end' : 'justify-start')}>
            <article
              aria-label={`Nachricht von ${m.author?.name ?? labelOf(MESSAGE_DIRECTION_LABELS, m.direction)}`}
              className={cn('flex w-full max-w-2xl flex-col gap-2 rounded-lg border px-3 py-2 text-sm', outbound ? 'bg-accent' : 'bg-card')}
            >
              <header className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{m.author?.name ?? 'Unbekannt'}</span>
                <span>({labelOf(MESSAGE_DIRECTION_LABELS, m.direction)})</span>
                <span className="numeric ml-auto">{formatDateTime(m.created_at)}</span>
              </header>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
              {m.attachments?.length > 0 && (
                <DocumentList
                  label={`Anhänge der Nachricht vom ${formatDateTime(m.created_at)}`}
                  documents={m.attachments}
                  hrefFor={(doc) => api.admin.documents.downloadUrl(doc.id)}
                />
              )}
            </article>
          </li>
        );
      })}
    </ol>
  );
}

function ReplyForm({ threadId }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const form = useForm({ resolver: zodResolver(messageReplySchema), defaultValues: { body: '' } });
  const submit = form.handleSubmit(async ({ body }) => {
    try {
      const updated = await api.admin.messages.reply(threadId, { body: body.trim(), files });
      queryClient.setQueryData(keys.admin.detail('messages', threadId), updated);
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('messages') });
      form.reset({ body: '' });
      setFiles([]);
      toast.success('Antwort gesendet');
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['body'] });
    }
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Antworten</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-3">
            <FormRootError />
            <TextareaField name="body" label="Antwort" rows={5} />
            <FileDropzone files={files} onFilesChange={setFiles} label="Anhänge" maxFiles={10} hint="bis zu 10 Dateien, je max. 20 MB" />
            <Button type="submit" className="w-fit" loading={form.formState.isSubmitting}>
              Antwort senden
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function ThreadControls({ thread, me }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const statusId = useId();
  const update = useMutation({
    mutationFn: (payload) => api.admin.messages.update(thread.id, payload),
    onSuccess: (updated) => {
      // PATCH answers with the thread incl. messages; keep the previous ones if an older API omits them.
      queryClient.setQueryData(keys.admin.detail('messages', thread.id), (prev) => ({ ...prev, ...updated, messages: updated?.messages ?? prev?.messages }));
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('messages') });
      toast.success('Vorgang aktualisiert');
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Der Vorgang konnte nicht aktualisiert werden.')),
  });
  const assignees = useQuery(adminQueries.messageAssignees(api));
  const assigneeId = useId();
  const assignedToMe = thread.assignee && String(thread.assignee.id) === String(me?.id);
  const options = [...(assignees.data ?? [])];
  if (thread.assignee && !options.some((u) => String(u.id) === String(thread.assignee.id))) options.unshift(thread.assignee);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor={assigneeId}>Zuständig</Label>
        <Select
          id={assigneeId}
          size="sm"
          value={thread.assignee ? String(thread.assignee.id) : ''}
          disabled={update.isPending || assignees.isPending}
          onChange={(e) => update.mutate({ assigned_to: e.target.value ? Number(e.target.value) : null })}
        >
          <option value="">niemand</option>
          {options.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.name}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={statusId}>Status</Label>
        <Select id={statusId} size="sm" value={thread.status} disabled={update.isPending} onChange={(e) => update.mutate({ status: e.target.value })}>
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </div>
      <div className="flex flex-wrap gap-2">
        {!assignedToMe && (
          <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ assigned_to: me.id })}>
            <UserCheck aria-hidden="true" /> Mir zuweisen
          </Button>
        )}
        {thread.assignee && (
          <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ assigned_to: null })}>
            <UserX aria-hidden="true" /> Zuweisung aufheben
          </Button>
        )}
      </div>
    </div>
  );
}
